import { createFileRoute, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { toast } from "sonner";
import { z } from "zod";
import { ProjectPage } from "../components/projects/ProjectPage";
import { ProjectHeading } from "../components/projects/ProjectHeading";
import { IssuePeek } from "../components/issues/IssuePeek";
import { useDeleteWithUndo } from "../components/issues/use-delete-with-undo";
import { TimelineView, type Zoom } from "../components/timeline/TimelineView";
import { Skeleton } from "../components/ui/skeleton";
import { Unavailable } from "../components/ui/states";
import { useMoveDates, useTimeline } from "../lib/timeline";
import { useProject } from "../lib/projects";
import { workspaceSlug } from "../lib/hosts";
import { usePlanAllows } from "../lib/workspaces";
import { NotInPlan } from "../components/plan/NotInPlan";
import { cn } from "../lib/cn";
import { strings } from "../lib/strings";
import { fill } from "../lib/words";
import type { PublicProject } from "../../shared/types/projects";

const words = strings.timeline;
const ZOOMS = ["weeks", "months", "quarters"] as const;

/** The timeline's address: its zoom, and the issue open in the peek. */
const timelineSearchSchema = z.object({
  zoom: z.enum(ZOOMS).optional().catch(undefined),
  issue: z.string().max(40).optional().catch(undefined),
});

function Timeline({ project, canManage }: { project: PublicProject; canManage: boolean }) {
  const search = useSearch({ from: "/admin/p/$projectKey/timeline" });
  const navigate = useNavigate({ from: "/admin/p/$projectKey/timeline" });
  const slug = workspaceSlug();
  // The timeline is a plan's feature (BUILD_SPEC §6.4): where it isn't included, the page says so and asks nothing.
  const allowed = usePlanAllows("timeline");
  const timeline = useTimeline(project.id, undefined, allowed);
  const move = useMoveDates(project.id);
  // Bars move for whoever may change the project's issues.
  const canEdit = useProject(project.id).data?.canEdit === true;
  const deleteWithUndo = useDeleteWithUndo(project.id);
  const zoom: Zoom = search.zoom ?? "months";
  const peeked =
    search.issue === undefined ? undefined : timeline.data?.issues.find((issue) => issue.key === search.issue);
  return (
    <div className="mx-auto max-w-7xl space-y-6 px-6 py-8">
      <header className="flex flex-col gap-4">
        <ProjectHeading project={project} projectKey={project.key} current="timeline" canManage={canManage} />
        <p className="text-sm text-[hsl(var(--muted-foreground))]">{words.body}</p>
        <div
          role="group"
          aria-label={words.zoomLabel}
          className="inline-flex w-fit rounded-md border border-[hsl(var(--border))] p-0.5"
        >
          {ZOOMS.map((each) => (
            <button
              key={each}
              type="button"
              aria-pressed={zoom === each}
              onClick={() =>
                void navigate({
                  search: (prev) => ({ ...prev, zoom: each === "months" ? undefined : each }),
                  replace: true,
                })
              }
              className={cn(
                "rounded px-2.5 py-1 text-sm focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:outline-none",
                zoom === each
                  ? "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]"
                  : "text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--accent))]",
              )}
            >
              {words.zooms[each]}
            </button>
          ))}
        </div>
      </header>
      {!allowed ? (
        <NotInPlan feature="timeline" />
      ) : timeline.data === undefined && timeline.isError ? (
        <Unavailable what={words.theTimeline} onRetry={() => void timeline.refetch()} />
      ) : timeline.data === undefined ? (
        <Skeleton className="h-72 w-full" />
      ) : timeline.data.issues.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[hsl(var(--border))] p-12 text-center">
          <p className="text-sm text-[hsl(var(--muted-foreground))]">{words.empty}</p>
        </div>
      ) : (
        <>
          {timeline.data.more && <p className="text-sm text-[hsl(var(--muted-foreground))]">{words.more}</p>}
          <TimelineView
            answer={timeline.data}
            zoom={zoom}
            canEdit={canEdit}
            onOpen={(issue) => void navigate({ search: (prev) => ({ ...prev, issue: issue.key }) })}
            onChange={(changes) =>
              move.mutate(changes, {
                onSuccess: (refused) =>
                  refused.length === 0
                    ? toast.success(
                        changes.length === 1
                          ? fill(words.moved, { key: changes[0]!.key })
                          : fill(words.movedSeveral, { count: changes.length }),
                      )
                    : toast.error(fill(words.refused, { keys: refused.join(", ") })),
                onError: () => toast.error(words.failed),
              })
            }
          />
        </>
      )}
      {peeked !== undefined && (
        <IssuePeek
          issueId={peeked.id}
          issueKey={peeked.key}
          slug={slug}
          projectId={project.id}
          pageNeeds={720}
          onClose={() => void navigate({ search: (prev) => ({ ...prev, issue: undefined }) })}
          onExpand={() =>
            void navigate({
              to: "/admin/p/$projectKey/$issueKey",
              params: { projectKey: project.key, issueKey: peeked.key },
            })
          }
          onRequestDelete={() =>
            deleteWithUndo.requestDelete(peeked.id, {
              onStart: () => void navigate({ search: (prev) => ({ ...prev, issue: undefined }) }),
            })
          }
        />
      )}
    </div>
  );
}

/** A project's timeline, its roadmap (BUILD_SPEC §12.5, §14.3). */
function Page() {
  const { projectKey } = useParams({ from: "/admin/p/$projectKey/timeline" });
  return (
    <ProjectPage projectKey={projectKey}>
      {(project, canManage) => <Timeline project={project} canManage={canManage} />}
    </ProjectPage>
  );
}

export const Route = createFileRoute("/admin/p/$projectKey/timeline")({
  validateSearch: timelineSearchSchema,
  component: Page,
});
